<template>
  <div class="app-shell">
    <router-view
      class="view"
      :cachedTheme="cachedTheme"
      @set-background-color="setBackgroundColor"
      @set-background-image="setBackgroundImage"
      @set-primary-button-color="setPrimaryButtonColor"
    />
  </div>

  <AppVersion />
  <ScrollHint :primary-button-color="primaryButtonColor" />
</template>

<script setup lang="ts">
import AppVersion from "@/components/AppVersion.vue";
import ScrollHint from "@/components/ScrollHint.vue";
import { theme } from "@entersekt/types/build/3-5-0/endpoint";
import { getStoredTheme, getCachedImages } from "@/store";
import { onMounted, onUnmounted, ref } from "vue";
import { startResizeReporter } from "@/utils/iframeResize";

let backgroundColor = ref("#ffffff");
let backgroundImage = ref("");
const primaryButtonColor = ref<string | undefined>();

function setBackgroundColor(background: string) {
  backgroundColor.value = background;
}

function setBackgroundImage(background: string) {
  backgroundImage.value = `url(${background})`;
}

function setPrimaryButtonColor(color?: string) {
  primaryButtonColor.value = color;
}

/**
 * Sync a concrete pixel viewport height for Android WebViews where
 * height:100% / vh can collapse to 0 until a definite size exists.
 */
function syncAppHeight() {
  const height =
    window.visualViewport?.height ||
    window.innerHeight ||
    document.documentElement.clientHeight;
  if (height > 0) {
    document.documentElement.style.setProperty("--app-height", `${height}px`);
  }
}

const cachedTheme = ref<theme>({});
const storedTheme = getStoredTheme();
if (storedTheme) {
  cachedTheme.value = storedTheme;
  primaryButtonColor.value = storedTheme.buttonPrimaryBackgroundColor;
}

const storedImages = getCachedImages();
if (storedImages) {
  const images = [];
  for (let i = 0; i < storedImages.length; i++) {
    images[i] = new Image();
    images[i].src = storedImages[i];
  }
}

if (cachedTheme.value.pageBackgroundColor) {
  setBackgroundColor(cachedTheme.value.pageBackgroundColor);
}

// Set as early as possible (before paint when the module evaluates).
syncAppHeight();

onMounted(() => {
  syncAppHeight();
  window.addEventListener("resize", syncAppHeight);
  window.visualViewport?.addEventListener("resize", syncAppHeight);
  startResizeReporter();
});

onUnmounted(() => {
  window.removeEventListener("resize", syncAppHeight);
  window.visualViewport?.removeEventListener("resize", syncAppHeight);
});
</script>

<style>
html,
body,
#app {
  margin: 0;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  min-height: var(--app-height, 100vh);
  overflow: hidden;
}

/*
 * Full-viewport flex shell: .view stays vertically centered when short,
 * and the shell scrolls when .view's content is taller than the frame.
 * Prefer --app-height / vh over height:100% alone — Android WebViews often
 * resolve percentage heights to 0px even when the visual viewport is valid.
 */
.app-shell {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  height: 100vh;
  height: 100dvh;
  height: var(--app-height, 100vh);
  min-height: 100vh;
  min-height: 100dvh;
  min-height: var(--app-height, 100vh);
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  background-color: v-bind(backgroundColor);
  background-image: v-bind(backgroundImage);
  background-size: cover;
  background-repeat: no-repeat;
}

.view {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;
  /* Vertical centering without justify-content:center on the shell,
   * so tall content is not clipped at the top. */
  margin-top: auto;
  margin-bottom: auto;

  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
</style>
